import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, Send, Bot, User, Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import FeatureDisclaimer from './FeatureDisclaimer';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AINutritionistChat = ({ token }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchChatHistory();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const history = response.data.messages || [];
      
      // Convert history to message format
      const formattedMessages = [];
      history.reverse().forEach(chat => {
        formattedMessages.push({
          role: 'user',
          content: chat.message,
          timestamp: chat.timestamp
        });
        formattedMessages.push({
          role: 'assistant',
          content: chat.response,
          timestamp: chat.timestamp
        });
      });
      
      setMessages(formattedMessages);
      setChatHistory(history);
    } catch (error) {
      console.error('Error fetching chat history:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    
    // Add user message immediately
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat/ask`,
        { message: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const SUGGESTED_QUESTIONS = [
    "What should I eat for breakfast?",
    "How can I increase my protein intake?",
    "Is my diet balanced?",
    "What are healthy snack options?",
    "How do I lose weight healthily?"
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden mb-8" style={{ height: '600px', display: 'flex', flexDirection: 'column' }} data-testid="ai-nutritionist-chat">
      {/* Disclaimer */}
      <div className="px-4 pt-3">
        <FeatureDisclaimer type="nutrition" />
      </div>
      {/* Header */}
      <div className="bg-gradient-to-r from-[#FF6600] to-[#FF8533] text-white p-4">
        <h2 className="text-lg font-bold flex items-center gap-2">
          <Bot size={24} />
          {t('aiChat.title')}
        </h2>
        <p className="text-sm opacity-90 mt-1">{t('aiChat.subtitle')}</p>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ maxHeight: 'calc(600px - 200px)' }}>
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Bot size={48} className="mx-auto text-[#FF6600] mb-4" />
            <h3 className="font-bold mb-4">{t('aiChat.welcome')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">{t('aiChat.startConversation')}</p>
            
            {/* Input Area - Moved to top when no messages */}
            <div className="mb-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={t('aiChat.placeholder')}
                  className="flex-1 p-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
                  disabled={loading}
                  data-testid="chat-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={loading || !inputMessage.trim()}
                  className="px-4 py-2 bg-[#FF6600] text-white rounded-lg hover:bg-[#E55C00] disabled:opacity-50"
                  data-testid="send-btn"
                >
                  <Send size={20} />
                </button>
              </div>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">Or try these quick questions:</p>
            <div className="space-y-2">
              {SUGGESTED_QUESTIONS.slice(0, 3).map((question, idx) => (
                <button
                  key={idx}
                  onClick={() => setInputMessage(question)}
                  className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg text-left text-sm hover:border-[#FF6600] hover:bg-orange-50"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message, idx) => (
          <div key={idx} className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {message.role === 'assistant' && (
              <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Bot size={18} className="text-[#FF6600]" />
              </div>
            )}
            <div
              className={`max-w-[70%] p-4 rounded-lg ${
                message.role === 'user'
                  ? 'bg-[#FF6600] text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              <p className="text-xs opacity-70 mt-1">
                {new Date(message.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            {message.role === 'user' && (
              <div className="w-8 h-8 rounded-full bg-[#FF6600] flex items-center justify-center flex-shrink-0">
                <User size={18} className="text-white" />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex gap-3 justify-start">
            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
              <Bot size={18} className="text-[#FF6600]" />
            </div>
            <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
              <Loader size={20} className="animate-spin text-[#FF6600]" />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
        <div className="flex gap-3">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about nutrition, diet, or meal planning..."
            className="flex-1 p-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
            disabled={loading}
          />
          <button
            onClick={sendMessage}
            disabled={loading || !inputMessage.trim()}
            className="px-4 py-2 bg-[#FF6600] text-white rounded-lg hover:bg-[#E55C00] disabled:opacity-50"
          >
            <Send size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AINutritionistChat;
